import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Layout, Menu, Button, Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type SiteConfig } from '@/apis/storefrontApi';
import { getStorefrontIcon } from '@/utils/storefrontIcons';
import ChatWidget from '@/components/ChatWidget';
import styles from './index.module.less';

const { Header, Content, Footer } = Layout;

const DEFAULT_BRAND = 'Sinolinkway';

const StorefrontLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);

  useEffect(() => {
    storefrontApi
      .getSiteConfig()
      .then(setSiteConfig)
      .catch(() => setSiteConfig(null));
  }, [i18n.language]);

  const navItems = siteConfig?.navItems ?? [
    { key: '/', label: 'Home', icon: 'HomeOutlined' },
    { key: '/products', label: 'Products', icon: 'AppstoreOutlined' },
    { key: '/about', label: 'About', icon: 'InfoCircleOutlined' },
    { key: '/contact', label: 'Contact', icon: 'MailOutlined' },
    { key: '/news', label: 'News', icon: 'FileTextOutlined' },
  ];

  const selectedKey = navItems.some(item => item.key === location.pathname)
    ? location.pathname
    : location.pathname.startsWith('/products/')
      ? '/products'
      : location.pathname.startsWith('/news/')
        ? '/news'
        : location.pathname.startsWith('/faq')
          ? '/faq'
          : '/';

  const brandName = siteConfig?.brandName ?? DEFAULT_BRAND;
  const footer = siteConfig?.footer;

  return (
    <Layout className={styles.layout}>
      <Header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to='/' className={styles.logo}>
            <img
              src={siteConfig?.logoImage || '/brand/mark-color.svg'}
              alt=''
              className={
                siteConfig?.logoImage ? styles.logoImg : styles.logoMark
              }
            />
            <span>{siteConfig?.logoText ?? brandName}</span>
          </Link>
          <nav className={styles.nav}>
            {navItems.map(item => (
              <Link
                key={item.key}
                to={item.key}
                className={
                  selectedKey === item.key
                    ? styles.navLinkActive
                    : styles.navLink
                }
              >
                {getStorefrontIcon(item.icon)}
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
          <div className={styles.actions}>
            <Button
              type='text'
              icon={<MenuOutlined />}
              className={styles.menuTrigger}
              onClick={() => setDrawerOpen(true)}
            />
            {/* 多语言、登录暂时隐藏，默认英文 */}
          </div>
        </div>
      </Header>

      <Drawer
        title={t('common.menu')}
        placement='right'
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        className={styles.drawer}
      >
        <Menu
          selectedKeys={[selectedKey]}
          mode='vertical'
          style={{ border: 'none' }}
          items={navItems.map(item => ({
            key: item.key,
            icon: getStorefrontIcon(item.icon),
            label: (
              <Link to={item.key} onClick={() => setDrawerOpen(false)}>
                {item.label}
              </Link>
            ),
          }))}
        />
      </Drawer>

      <ChatWidget />

      <Content className={styles.content}>
        <Outlet />
      </Content>

      <Footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerGrid}>
            <div>
              <div className={styles.footerBrand}>
                <img
                  src='/brand/mark-white.svg'
                  alt=''
                  className={styles.footerMark}
                />
                <span>{footer?.brandName ?? brandName}</span>
              </div>
              <p className={styles.footerDesc}>{footer?.description ?? ''}</p>
            </div>
            <div>
              <div className={styles.footerTitle}>{t('common.quickLinks')}</div>
              <div className={styles.footerLinks}>
                {(
                  footer?.quickLinks ?? [
                    { label: 'Products', path: '/products' },
                    { label: 'About', path: '/about' },
                    { label: 'Contact', path: '/contact' },
                    { label: 'News', path: '/news' },
                  ]
                ).map(link => (
                  <Link key={link.path} to={link.path}>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className={styles.footerTitle}>{t('common.contact')}</div>
              {footer?.contact && (
                <>
                  <p className={styles.footerContact}>{footer.contact.email}</p>
                  <p className={styles.footerContact}>{footer.contact.phone}</p>
                  {footer.contact.whatsapp && (
                    <p className={styles.footerContact}>WhatsApp: {footer.contact.whatsapp}</p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.footerBottom}>
            {(
              footer?.copyright ??
              `© ${new Date().getFullYear()} ${brandName}. All rights reserved.`
            ).replace('{year}', String(new Date().getFullYear()))}
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default StorefrontLayout;
